import type { Product } from "@digital-garden/shared";

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  role: "customer" | "owner" | "support";
}

export interface ContentItem {
  id: string;
  slug: string;
  title: string;
  type: "book" | "guide" | "research" | "collection" | "insight";
  summary: string;
  bodyMarkdown: string;
  access: "public" | "member" | "paid" | "private";
  ownershipBasis: string;
  status: string;
  readingMinutes: number;
  tags: string[];
  publishedAt: string | null;
}

export interface Entitlement {
  id: string;
  status: string;
  downloadCount: number;
  downloadLimit: number | null;
  expiresAt: string | null;
  product: Product;
}

export interface ProductFile {
  id: string;
  productId: string;
  version: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
  status: string;
}

export interface CommunityAuthor {
  id: string;
  displayName: string;
}

export interface ProductReview {
  id: string;
  productId: string;
  rating: number;
  title: string;
  body: string;
  status: "pending" | "published" | "rejected" | "hidden";
  verifiedPurchase: boolean;
  author: CommunityAuthor;
  moderationNote: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DiscussionThread {
  id: string;
  productId: string;
  title: string;
  body: string;
  status: "pending" | "published" | "locked" | "rejected" | "hidden";
  replyCount: number;
  author: CommunityAuthor;
  moderationNote: string | null;
  lastActivityAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DiscussionReply {
  id: string;
  threadId: string;
  body: string;
  status: "pending" | "published" | "rejected" | "hidden";
  author: CommunityAuthor;
  moderationNote: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CommunityPolicy {
  enabled: boolean;
  reviewsRequirePurchase: boolean;
  dailyReviewLimit: number;
  dailyThreadLimit: number;
  dailyReplyLimit: number;
  dailyFlagLimit: number;
}

export interface ProductCommunityData {
  product?: { id: string; slug: string; name: string };
  summary: {
    total: number;
    average: number;
    distribution: Record<1 | 2 | 3 | 4 | 5, number>;
  };
  reviews: ProductReview[];
  threads: DiscussionThread[];
  viewer: { canReview: boolean; ownReview: ProductReview | null };
  policy: CommunityPolicy;
}

export interface MyCommunityData {
  reviews: Array<ProductReview & { product: { name: string; slug: string } }>;
  threads: Array<
    DiscussionThread & { product: { name: string; slug: string } }
  >;
  replies: Array<DiscussionReply & { threadTitle: string }>;
  flags: Array<{
    id: string;
    targetType: string;
    targetId: string;
    reason: string;
    details: string;
    status: string;
    createdAt: string | null;
  }>;
}

export interface AcademyProductLink {
  id: string;
  name: string;
  slug: string;
}

export interface AcademyCertificate {
  id: string;
  verificationCode: string;
  courseTitle: string;
  recipientName: string;
  issuedAt: string | null;
  revokedAt: string | null;
  revocationNote: string | null;
  valid: boolean;
}

export interface AcademyQuizSummary {
  id: string;
  title: string;
  instructions: string;
  passPercent: number;
  maxAttempts: number;
  questionCount: number;
  attemptsUsed: number;
  bestScore: number;
  passed: boolean;
}

export interface AcademyLesson {
  id: string;
  slug: string;
  title: string;
  summary: string;
  bodyMarkdown: string;
  lessonType: "article" | "exercise" | "resource";
  estimatedMinutes: number;
  required: boolean;
  sortOrder: number;
  progress: {
    status: "in_progress" | "completed";
    completedAt: string | null;
    updatedAt: string | null;
  } | null;
  quiz: AcademyQuizSummary | null;
}

export interface AcademyModule {
  id: string;
  title: string;
  summary: string;
  sortOrder: number;
  lessons: AcademyLesson[];
}

export interface AcademyCourse {
  id: string;
  slug: string;
  title: string;
  summary: string;
  descriptionMarkdown: string;
  accessLevel: "public" | "member" | "paid";
  productId: string | null;
  difficulty: "beginner" | "intermediate" | "advanced";
  estimatedMinutes: number;
  certificateEnabled: boolean;
  status: string;
  sortOrder: number;
  publishedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  product: AcademyProductLink | null;
  moduleCount?: number;
  lessonCount?: number;
  modules?: AcademyModule[];
  viewer?: {
    enrolled: boolean;
    enrollmentStatus: string | null;
    progressPercent: number;
  };
}

export interface AcademyCourseDetail {
  course: AcademyCourse & { modules: AcademyModule[] };
  viewer: {
    authenticated: boolean;
    allowed: boolean;
    enrolled: boolean;
    enrollmentId: string | null;
    enrollmentStatus: string | null;
    progressPercent: number;
    contentAvailable: boolean;
  };
}

export interface AcademyEnrollment {
  id: string;
  status: "active" | "completed" | "revoked";
  progressPercent: number;
  enrolledAt: string | null;
  completedAt: string | null;
  lastActivityAt: string | null;
  course: AcademyCourse;
  certificate: AcademyCertificate | null;
}

export interface AcademyQuiz {
  id: string;
  lessonId: string;
  title: string;
  instructions: string;
  passPercent: number;
  maxAttempts: number;
  questions: Array<{
    id: string;
    prompt: string;
    options: string[];
    sortOrder: number;
  }>;
}

export interface AcademyQuizPayload {
  quiz: AcademyQuiz;
  attempts: {
    used: number;
    remaining: number;
    bestScore: number;
    passed: boolean;
  };
}

export interface AcademyCompletion {
  progressPercent: number;
  status: "active" | "completed";
  requiredLessons: number;
  completedLessons: number;
  requiredQuizzes: number;
  passedQuizzes: number;
  certificate: AcademyCertificate | null;
}

export interface ServiceBookingPolicy {
  enabled: boolean;
  minimumLeadHours: number;
  maximumHorizonDays: number;
  maximumRequestedHours: number;
  slotMinutes: number;
}

export interface ServiceOffering {
  id: string;
  slug: string;
  title: string;
  summary: string;
  descriptionMarkdown: string;
  category: string;
  deliveryMode: "remote" | "onsite" | "hybrid";
  durationMinutes: number;
  startingPriceUsdCents: number | null;
  startingPriceBdtPaisa: number | null;
  status: string;
  sortOrder: number;
  publishedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ServiceBookingEvent {
  id: string;
  eventType: string;
  fromStatus: string | null;
  toStatus: string | null;
  note: string;
  visibility: "customer" | "owner";
  metadata: Record<string, unknown>;
  createdAt: string | null;
  actor: { id: string | null; displayName: string | null };
}

export interface ServiceBooking {
  id: string;
  status:
    | "requested"
    | "reviewing"
    | "quoted"
    | "accepted"
    | "declined"
    | "scheduled"
    | "in_progress"
    | "completed"
    | "rejected"
    | "cancelled";
  scope: string;
  preferredStartUtc: string;
  preferredEndUtc: string;
  customerTimezone: string;
  contactMethod: "email" | "phone" | "whatsapp" | "google_meet" | "other";
  contactValue: string;
  budget: {
    currency: "USD" | "BDT" | "OTHER";
    amountMinor: number | null;
  } | null;
  customerNote: string;
  quote: {
    currency: "USD" | "BDT";
    amountMinor: number;
    expiresAt: string | null;
    note: string | null;
    expired: boolean;
  } | null;
  schedule: { startUtc: string; endUtc: string } | null;
  cancellationReason: string | null;
  service: {
    id: string;
    slug: string;
    title: string;
    deliveryMode: string;
    durationMinutes: number;
  };
  events: ServiceBookingEvent[];
  createdAt: string | null;
  updatedAt: string | null;
  reviewedAt: string | null;
  quotedAt: string | null;
  acceptedAt: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
}

export interface InboxItem {
  id: string;
  source: "notification" | "announcement";
  type: string;
  title: string;
  body: string;
  actionUrl: string | null;
  entityType: string | null;
  entityId: string | null;
  priority: "info" | "action" | "urgent";
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string | null;
  expiresAt: string | null;
}

export interface InboxPreferences {
  inAppEnabled: boolean;
  categories: Record<string, boolean>;
}

export interface InboxPayload {
  items: InboxItem[];
  unreadCount: number;
  preferences: InboxPreferences;
}

export interface CustomerOrder {
  id: string;
  provider: string;
  status: string;
  currency: "USD" | "BDT";
  amount: number;
  buyerEmail: string;
  createdAt: string | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, details?: unknown) {
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const apiOrigin =
  import.meta.env.VITE_API_ORIGIN ??
  (import.meta.env.DEV ? "http://127.0.0.1:8787" : window.location.origin);

export async function api<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...requestInit } = init;
  const headers = new Headers(requestInit.headers);
  let body = requestInit.body;
  if (json !== undefined) {
    headers.set("content-type", "application/json");
    body = JSON.stringify(json);
  }
  const options: RequestInit = {
    ...requestInit,
    headers,
    credentials: "include",
  };
  if (body !== undefined) options.body = body;
  const response = await fetch(apiOrigin + path, options);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson
    ? ((await response.json()) as unknown)
    : await response.text();

  if (!response.ok) {
    const record =
      typeof payload === "object" && payload !== null
        ? (payload as Record<string, unknown>)
        : {};
    throw new ApiError(
      response.status,
      String(record.error ?? "REQUEST_FAILED"),
      payload,
    );
  }

  if (!isJson) {
    throw new ApiError(response.status, "INVALID_API_RESPONSE", {
      path,
      contentType,
    });
  }

  return payload as T;
}

export const errorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    const messages: Record<string, string> = {
      AUTH_REQUIRED: "Sign in to continue.",
      OAUTH_NOT_CONFIGURED:
        "Google sign-in is awaiting production credentials.",
      MANUAL_PAYMENTS_DISABLED:
        "Bangladesh manual payments are not enabled yet.",
      LEMONSQUEEZY_CHECKOUT_CONFIGURATION:
        "International checkout is awaiting merchant activation.",
      AI_OR_DATABASE_NOT_CONFIGURED: "The live AI route is not activated yet.",
      MODEL_ROUTE_NOT_CONFIGURED:
        "This intelligence route is not configured yet.",
      AI_SUBSCRIPTION_REQUIRED: "This route requires an active AI plan.",
      FREE_DAILY_LIMIT_REACHED: "Today’s free AI allowance has been used.",
      AI_QUOTA_EXHAUSTED: "This plan’s current AI allowance has been used.",
      AMOUNT_MISMATCH:
        "The submitted amount does not match the verified product total.",
      DUPLICATE_TRANSACTION: "This transaction ID has already been submitted.",
      TURNSTILE_REQUIRED: "Complete human verification before submitting.",
      TURNSTILE_NOT_CONFIGURED:
        "Human verification is required but its production key is not configured.",
      TURNSTILE_REJECTED:
        "Human verification was rejected. Reset it and try again.",
      TURNSTILE_ACTION_MISMATCH:
        "The verification token does not match this protected action.",
      TURNSTILE_HOSTNAME_MISMATCH:
        "Human verification was issued for a different hostname.",
      TURNSTILE_VERIFICATION_UNAVAILABLE:
        "Human verification is temporarily unavailable.",
      VERIFIED_PURCHASE_REQUIRED:
        "Only a verified purchaser can review this product.",
      COMMUNITY_DAILY_LIMIT_REACHED:
        "The daily community contribution limit has been reached.",
      COMMUNITY_DISABLED: "Community participation is temporarily disabled.",
      DISCUSSION_NOT_OPEN: "This discussion is not open for new replies.",
      SELF_FLAG_NOT_ALLOWED: "You cannot report your own contribution.",
      ALREADY_FLAGGED: "You have already reported this contribution.",
      COURSE_ENTITLEMENT_REQUIRED:
        "Purchase the linked product before enrolling in this course.",
      COURSE_ACCESS_REQUIRED:
        "This course is not available to the current account.",
      COURSE_NOT_AVAILABLE: "This course is not available for enrollment.",
      ACTIVE_ENROLLMENT_REQUIRED:
        "Enroll in this course before recording lesson progress.",
      QUIZ_NOT_AVAILABLE:
        "This quiz is not available for the current enrollment.",
      QUIZ_ATTEMPT_LIMIT_REACHED:
        "The maximum number of quiz attempts has been reached.",
      QUIZ_ALL_QUESTIONS_REQUIRED:
        "Answer every quiz question before submitting.",
      QUIZ_OPTION_INVALID: "One selected quiz option is invalid.",
      CERTIFICATE_NOT_FOUND: "No certificate matches this verification code.",
      SERVICE_BOOKING_DISABLED:
        "New service requests are temporarily disabled.",
      SERVICE_NOT_AVAILABLE: "This service is not available for new requests.",
      BOOKING_WINDOW_INVALID: "Choose a valid preferred start and end time.",
      BOOKING_LEAD_TIME_REQUIRED:
        "The preferred time is too soon for the current booking policy.",
      BOOKING_HORIZON_EXCEEDED:
        "The preferred time is beyond the available booking horizon.",
      BOOKING_WINDOW_TOO_LONG:
        "The requested time window is longer than the booking policy allows.",
      DUPLICATE_BOOKING_REQUEST:
        "A matching active request already exists for this service and start time.",
      BOOKING_QUOTE_NOT_ACTIONABLE:
        "This quote can no longer be accepted or declined.",
      BOOKING_QUOTE_EXPIRED:
        "This quote has expired. The owner must issue a new quote.",
      BOOKING_CANNOT_BE_CANCELLED:
        "This booking can no longer be cancelled from My Bookings.",
      BOOKING_SCHEDULE_CONFLICT:
        "The selected service time conflicts with another confirmed booking.",
    };
    return (
      messages[error.code] ?? error.code.replaceAll("_", " ").toLowerCase()
    );
  }
  return error instanceof Error
    ? error.message
    : "An unexpected error occurred.";
};
